import React, { useMemo, useState, memo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { SeedStatus } from '../system/diagnostics/SeedStatus';
import { LoadedModulesWidget } from '../system/diagnostics/LoadedModulesWidget';
import { OrphanStatus } from '../system/diagnostics/OrphanStatus';
import { SsotStatus } from '../system/diagnostics/SsotStatus';
import { STORAGE_QUOTA_MB } from '../../../utils/storageQuota';
import { CategoryCoverageWidget } from '../system/diagnostics/CategoryCoverageWidget';
import { computeLiveSystemStats } from '../../../governance';
import { QUESTIONS_BY_FAMILY, TAXONOMY } from '../../../content';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { AdminTabs } from '../shared/AdminTabs';
import { Database, GitBranch, Cpu, Clock, HardDrive, Activity, Hash, Tag, ShieldCheck } from 'lucide-react';
import { dataLayer } from '../../../data/dataLayer';
const tabs = [{
  id: 'seeds',
  label: 'Seeds',
  icon: <Database className="w-4 h-4" data-id="element-7101" data-mp="2htsosqsvc7v5" />
}, {
  id: 'system',
  label: 'Systeem',
  icon: <Cpu className="w-4 h-4" data-id="element-7102" data-mp="2hvvbp1yq5kvn" />
}, {
  id: 'orphans',
  label: 'Orphans',
  icon: <Activity className="w-4 h-4" data-id="element-7103" data-mp="234d0dhgt8eim" />
}, {
  // Naast Orphans, want het is dezelfde soort vraag: waar staat iets dubbel of
  // los van zijn bron? Orphans kijkt naar data, dit naar definities.
  id: 'ssot',
  label: 'Bronnen',
  icon: <ShieldCheck className="w-4 h-4" data-id="element-7104" data-mp="1u6kjw9zcr0tn" />
}] as const;
type TabId = (typeof tabs)[number]['id'];
function ResourceBar({
  label,
  value,
  color
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  const isNumber = typeof value === 'number';
  const displayValue = isNumber ? `${value}%` : value;
  const percentValue = isNumber ? value : 0;
  return <div data-id="element-7105" data-mp="3elcp633y8b7w">
      <div className="flex justify-between text-xs mb-1.5" data-id="element-7106" data-mp="3ekun2pulv2s2">
        <span className="text-gray-700 font-medium" data-id="element-7107" data-mp="3jjeakuoukmmj">{label}</span>
        <span className={`font-semibold ${isNumber && value > 80 ? 'text-red-500' : isNumber && value > 60 ? 'text-yellow-600' : 'text-gray-600'}`} data-id="element-7108" data-mp="3i1z5xt7ymkyp">
          
          {displayValue}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-7109" data-mp="383jcvkpdh1mk">
        <div className={`${color} h-2 rounded-full transition-all`} style={{
        width: `${percentValue}%`
      }} data-id="element-7110" data-mp="3842v72ixps14" />
        
      </div>
    </div>;
}
function SystemPanel() {
  const {
    accounts
  } = useAppStore();
  const diagnostics = useMemo(() => {
    const storageStats = dataLayer.getStorageStats();
    const storageMb = storageStats.usedMb;
    const activeAccounts = accounts?.accountsById ? Object.values(accounts.accountsById).filter(a => Date.now() - (a.lastActive || 0) < 60 * 60 * 1000).length : 0;
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch {

      // Performance timing API may be unavailable
    }
    return {
      storageMb,
      activeAccounts,
      responseTime: Math.max(10, responseTime)
    };
  }, [accounts]);
  const resourceMetrics = useMemo(() => {
    let memoryUsage = 0;
    try {
      const perfWithMemory = window.performance as Performance & {
        memory?: {
          usedJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      };
      if (perfWithMemory.memory) {
        memoryUsage = Math.round(perfWithMemory.memory.usedJSHeapSize / perfWithMemory.memory.jsHeapSizeLimit * 100);
      } else {
        memoryUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / STORAGE_QUOTA_MB * 100));
      }
    } catch {

      // Memory API may be unavailable in some browsers
    }
    const storageUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / STORAGE_QUOTA_MB * 100));
    return {
      memory: memoryUsage,
      storage: storageUsage
    };
  }, [diagnostics.storageMb]);
  const environmentRows = [{
    label: 'Build mode',
    value: typeof import.meta !== 'undefined' && import.meta.env?.MODE ? import.meta.env.MODE : 'development'
  }, {
    label: 'Lokale opslag',
    value: `${diagnostics.storageMb} MB`
  }, {
    label: 'Actieve accounts',
    value: diagnostics.activeAccounts.toString()
  }];
  return <div className="space-y-6" data-id="element-7111" data-mp="2z2tmzpq2wpaf">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-7112" data-mp="2bch97y85jybx">
        <StatCard label="Laadtijd" value={`${diagnostics.responseTime}ms`} subtitle="gemiddeld" icon={<Clock className="w-5 h-5" data-id="element-7114" data-mp="26f799on5049u" />} color="amber" data-id="element-7113" data-mp="2bcphtdx8shhb" />
        
        <StatCard label="Opslag" value={`${diagnostics.storageMb} MB`} subtitle="Lokale opslag" icon={<HardDrive className="w-5 h-5" data-id="element-7116" data-mp="2gbal5m7sjh9z" />} color="blue" data-id="element-7115" data-mp="2laww1p05clfm" />
        
        <StatCard label="Actieve Sessies" value={diagnostics.activeAccounts} subtitle="Laatste uur" icon={<Activity className="w-5 h-5" data-id="element-7118" data-mp="m8u7m00rw7a" />} color="purple" data-id="element-7117" data-mp="keauc4ecc5b" />
        
      </div>

      <LoadedModulesWidget data-id="element-7119" data-mp="3rqfmomw6r9s0" />

      <CategoryCoverageWidget data-id="element-7120" data-mp="353x41mkot0f" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-id="element-7121" data-mp="33ss5yhe5f7q">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7122" data-mp="83nnce878qyl">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7123" data-mp="3q9tkwbb4zdh1">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-7124" data-mp="3qbwgdav25yt5">
              Systeembronnen
            </h3>
          </div>
          <div className="p-4 sm:p-5 space-y-4" data-id="element-7125" data-mp="lutsapu8zgsv">
            <ResourceBar label="Geheugen" value={resourceMetrics.memory} color="bg-purple-500" data-id="element-7126" data-mp="lw3pzdx6umyz" />
            
            <ResourceBar label="Opslag" value={resourceMetrics.storage} color="bg-green-500" data-id="element-7127" data-mp="qwj0zytzzids" />
            
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7128" data-mp="h43jt3qiyxty">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7129" data-mp="14to6nxpsuf8i">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-7130" data-mp="19kwgguv1k2m7">Omgeving</h3>
          </div>
          <div className="divide-y divide-gray-50" data-id="element-7131" data-mp="19mqzg3mr5knn">
            {environmentRows.map((row, i) => <div key={i} className="px-4 sm:px-5 py-3 flex justify-between items-center text-sm" data-id="element-7132" data-mp="1jesimtw5m9qj" data-mpi={i}>
              
                <span className="text-gray-600" data-id="element-7133" data-mp="1ag884g0qz6de">{row.label}</span>
                <span className="font-medium text-gray-900" data-id="element-7134" data-mp="1aedl8w5x146o">{row.value}</span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
export function DiagnosticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('seeds');
  const stats = useMemo(() => computeLiveSystemStats(), []);
  return <PageLayout title="Systeem Diagnostiek" subtitle="Technische status en systeembronnen" actions={<ConnectionBadge status="live" data-id="element-7136" data-mp="rpyywm6jmec7" />} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Diagnostiek'
  }]} data-id="element-7135" data-mp="vp7nzdqn1o1f">
      
      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-7137" data-mp="hrstvvpfws34">
        <StatCard label="Totaal Vragen" value={stats.totalQuestions.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-7139" data-mp="2ojdsblopgh3" />} color="blue" data-id="element-7138" data-mp="csq0j8itoaaz" />
        
        <StatCard label="Families" value={QUESTIONS_BY_FAMILY.size} icon={<Tag className="w-5 h-5" data-id="element-7141" data-mp="7mt9cnbbmj38" />} color="purple" data-id="element-7140" data-mp="2nzsqm0qmbyu" />
        
        <StatCard label="Taxonomie" value={TAXONOMY.length} icon={<GitBranch className="w-5 h-5" data-id="element-7143" data-mp="co0iapp79obh" />} color="emerald" data-id="element-7142" data-mp="7pfhop61d2mi" />
        
        <StatCard label="Geheugen" value={`${Math.round(stats.memoryUsageMB)} MB`} icon={<Cpu className="w-5 h-5" data-id="element-7145" data-mp="3g3fpzka3vltg" />} color="amber" data-id="element-7144" data-mp="hmbrqzbl1wt6" />
        
      </div>

      <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-7146" data-mp="3g58tiqpf9px8" />
      

      <div className="mt-4" data-id="element-7147" data-mp="3l0eixzi19mq2">
        {activeTab === 'seeds' && <SeedStatus data-id="element-7148" data-mp="3q0kbrj01hf9q" />}
        {activeTab === 'system' && <SystemPanel data-id="element-7149" data-mp="3pzr0q5u1s3ls" />}
        {activeTab === 'orphans' && <OrphanStatus data-id="element-7150" data-mp="3utdey2fv0fjp" />}
        {activeTab === 'ssot' && <SsotStatus data-id="element-7151" data-mp="3uu4zmi7x4sv7" />}
      </div>
    </PageLayout>;
}